/* IMPLEMENTATION REVENUE ENGINE
   Computes implementation fees from source revenue (AE Quota, Company Quota, Misc Rev, or standalone).
   Supports immediate recognition (hits IS directly) and deferred (cohort-based 12-month recognition). */

import { addMonths } from './dateUtils';
import { recognizeExistingBook } from './existingBookRecognition';
import { implOpeningBalance } from '../utils/implDeferred';
import { isPctNewAcvBasis } from '../utils/implBasis';

const r2 = n => Math.round(n * 100) / 100;

/**
 * @param {Array} lines           — implementation_lines rows from DB
 * @param {Object} sourceData     — { aeQuota: { monthlyNewACV }, companyQuota: { newACV }, miscRevenue: { [lineId]: { amounts } } }
 * @param {string[]} forecastMonths — 12 month strings ["2026-01"..."2026-12"]
 * @param {string} closeMonth      — last actuals month e.g. "2025-12"
 * @param {Array}  [warningsOut]  — ★ PART B: optional SINK for named refusals. ⛔ A separate
 *   array rather than a key on the returned map, because every consumer walks that map with
 *   `Object.entries` (orchestrator:137, :206; runFullModel's deposit) and a `__warnings` key
 *   would be iterated as though it were a line. Mirrors the warnings FAMILY that runFullModel
 *   already projects: missingSweepTarget / missingChartOfAccounts / missingCloseMonth, and
 *   payroll's missingAnchorWarnings / sutaWarnings / fedTaxWarnings.
 * @param {Object} [opts]        -- W-081: { bsActuals } -- the GL balance sheet, so a line LINKED to its real deferred-revenue
 *   account opens at THAT ACCOUNT'S close-month actual (utils/implDeferred.js implOpeningBalance). Omitted -> the manual
 *   opening_bs_balance, exactly as before (every caller that does not pass it is unchanged).
 * @returns {Object} — keyed by line.id, each with { name, enabled, recognition_mode,
 *   target_gl_line_key, bs_line_key, newClients, grossImpl, netImpl, gaapRevenue, deferredBalance, sourceNewACV }
 */
function computeImplementationRevenue(lines, sourceData, forecastMonths, closeMonth, warningsOut, opts) {
  const result = {};
  const warnings = Array.isArray(warningsOut) ? warningsOut : [];
  if (!lines || !lines.length) return result;

  /* ★★ THE SPAN IS NOW THE CALLER'S WINDOW — full note in computeAEQuotaDR.js. This used to
     be a fixed 24-month list bounded by the LITERAL 24, so a window longer than 24 months
     found NO KEY in the emit loop's `x[m] || 0` and rendered $0.00 from month 25. B6's defect
     at a different number. ⛔ Not replaced by a larger literal. */

  lines.forEach(line => {
    if (!line.enabled) return;

    const sourceType = line.source_type || 'standalone';
    const avgPrice = parseFloat(line.avg_price) || 0;
    const implFee = parseFloat(line.impl_fee) || 0;
    /* ★★ N-B — A DELIBERATE 0 IS AN ANSWER, NOT A BLANK. This read
     *     const takeRate = parseFloat(line.take_rate) || 1;
     *   and `parseFloat("0") === 0` is FALSY, so the `|| 1` arm fired and an operator who
     *   answered "0% of new clients buy implementation" got **100%** — the MAXIMUM value,
     *   substituted for their answer. Measured: a forged line at take_rate 0 produced the
     *   IDENTICAL net implementation to take_rate 1 (20,000/month on cid=36's inputs).
     * ★ IT WAS FOUND BY A MUST-DIFFER CONTROL FAILING TO DIFFER, which is the only way a
     *   defect of this shape surfaces: the wrong answer is a PLAUSIBLE number.
     * ⇒ ★ THE SHAPE IS §E1 INVERTED. §E1 says a blank must be FLAGGED, never silently
     *   defaulted; here a real, deliberate ZERO was silently replaced by the maximum. The
     *   operator's answer was discarded either way.
     * ⓘ AND THE COLUMN ALREADY DISTINGUISHES THE TWO STATES: `take_rate numeric NULL
     *   DEFAULT 1.0`. NULL means "never answered" (the default then applies); 0 means
     *   "answered, and the answer is none". `parseFloat(x) || 1` cannot tell them apart.
     * ★ NORMALISED EXPLICITLY, MIRRORING `length_months` TWELVE LINES BELOW — the in-file
     *   precedent, which never uses `|| n`: name the absent states, convert, then decide.
     *   `Number` rather than `parseFloat` for the same reason it uses it: it refuses a
     *   partially-numeric string outright instead of silently truncating one.
     * ⓘ NULL / undefined / '' -> 1, which reproduces the column's own DEFAULT. That is the
     *   honest answer for "never answered": the engine agrees with the schema rather than
     *   inventing a third opinion. Measured: 0 live rows carry a NULL take_rate, and the UI
     *   always sends a number, so this arm is unusual rather than routine.
     * ⛔ NO CLAMP AND NO REFUSAL HERE — see the build record. A negative or >100% rate is
     *   arithmetically well-defined and VISIBLY wrong (net implementation exceeds gross on
     *   the waterfall); silently clamping it would be this same defect in a new costume. */
    const rawTake = line.take_rate === null || line.take_rate === undefined || line.take_rate === ''
      ? null : Number(line.take_rate);
    const takeRate = Number.isFinite(rawTake) ? rawTake : 1;
    const recognitionMode = line.recognition_mode || 'immediate';
    const startMode = line.start_mode || 'zero';
    // W-081: LINKED to the real account -> its close-month actual (derived); Synthetic -> the manual field.
    const openingBsBalance = implOpeningBalance(line, closeMonth, opts ? opts.bsActuals : undefined);
    const existingTotalImpl = line.existing_total_impl || {};
    const existingMonthlyRev = line.existing_monthly_rev || {};
    const standaloneAmounts = line.standalone_amounts || {};

    /* ★ PART B — THE PER-COHORT TERM, normalised ONCE here and read everywhere below.
     * `Number.isInteger(n) && n > 0` is the WHOLE predicate: it rejects null, undefined, 0,
     * negatives, fractions and NaN in one expression, so there is ONE rule in ONE place.
     * ⛔ Two guards for one rule is the drift shape (§6), which is also why Part A added NO
     *   `CHECK (length_months > 0)`: `NULL > 0` is NULL and a CHECK PASSES on it, so a CHECK
     *   is silent on exactly the MISSING case — the only case that matters here.
     * ⓘ falsy termMonths === "refuse", and it is consulted ONLY on the deferred path below.
     *   An IMMEDIATE line recognises netImpl in the month it is booked and amortises nothing,
     *   so requiring a length there would fire on CORRECT data (§12.5 shape 10). */
    const rawLen = line.length_months === null || line.length_months === undefined
      ? null : Number(line.length_months);
    const termMonths = Number.isInteger(rawLen) && rawLen > 0 ? rawLen : 0;

    /* 2026-10-27 -- THE FEE BASIS ("% of new ACV"; Brian Q-A..Q-H; utils/implBasis.js owns the vocabulary). NULL or 'per_client'
     * = the per-client method below, exactly as before; 'pct_new_acv' = new ACV x pct_of_new_acv, the take rate NOT applied (Q-A:
     * the percentage already is the expected fee). The percentage is a fraction (0.2 = 20%); 0 is an answer. A % line with no
     * percentage cannot be stored through the app (the server's resulting-row rule); if one arrives anyway it is REFUSED here the
     * Part B way -- warned into the same sink, the line excluded and named -- never a default 0 or 100%, never a throw. */
    const pctBasis = isPctNewAcvBasis(line.impl_basis);
    const rawPct = line.pct_of_new_acv === null || line.pct_of_new_acv === undefined || line.pct_of_new_acv === ''
      ? null : Number(line.pct_of_new_acv);
    const pctOfNewAcv = Number.isFinite(rawPct) ? rawPct : null;
    if (pctBasis && pctOfNewAcv === null) {
      const msg = `Implementation line "${line.name}" (id ${line.id}) computes its fee as a % of new ACV but has no percentage. ` +
        `pct_of_new_acv = ${line.pct_of_new_acv === null || line.pct_of_new_acv === undefined ? 'not set' : JSON.stringify(line.pct_of_new_acv)}` +
        ` -- enter the percentage (0 is an answer). The line is EXCLUDED from the forecast until it is entered; no percentage is assumed.`;
      warnings.push({ lineId: line.id, name: line.name, pct_of_new_acv: line.pct_of_new_acv ?? null, message: msg });
      console.warn('[computeImplementationRevenue] ' + msg);
      return; // contributes nothing -- no result entry, no deposit, no leaf value
    }

    const sourceNewACV = {};
    const newClients = {};
    const grossImpl = {};
    const netImpl = {};
    const gaapRevenue = {};
    const deferredBalance = {};

    // Initialize all 24 months (for deferred) or just forecastMonths (for immediate)
    const months = recognitionMode === 'deferred' ? forecastMonths : forecastMonths;
    months.forEach(m => {
      sourceNewACV[m] = 0;
      newClients[m] = 0;
      grossImpl[m] = 0;
      netImpl[m] = 0;
      gaapRevenue[m] = 0;
      deferredBalance[m] = 0;
    });

    // ── Get source new ACV per month ──
    forecastMonths.forEach(m => {
      if (sourceType === 'ae_quota') {
        sourceNewACV[m] = r2(sourceData.aeQuota?.monthlyNewACV?.[m] || 0);
      } else if (sourceType === 'company_quota') {
        sourceNewACV[m] = r2(sourceData.companyQuota?.newACV?.[m] || 0);
      } else if (sourceType === 'misc_rev') {
        sourceNewACV[m] = r2(sourceData.miscRevenue?.[line.source_id]?.amounts?.[m] || 0);
      } else {
        // standalone
        sourceNewACV[m] = r2(parseFloat(standaloneAmounts[m]) || 0);
      }
    });

    // ── Calculate net implementation revenue per month ──
    forecastMonths.forEach(m => {
      if (pctBasis) {
        // % of new ACV: one product, one rounding; no client count, no take rate (Q-A).
        newClients[m] = 0;
        netImpl[m] = r2(sourceNewACV[m] * pctOfNewAcv);
        grossImpl[m] = netImpl[m];
        return;
      }
      newClients[m] = avgPrice > 0 ? Math.round(sourceNewACV[m] / avgPrice) : 0;
      grossImpl[m] = r2(newClients[m] * implFee);
      netImpl[m] = r2(grossImpl[m] * takeRate);
    });

    // ── Recognition ──
    if (recognitionMode === 'immediate') {
      // Immediate: gaapRevenue = netImpl, no deferred balance
      forecastMonths.forEach(m => {
        gaapRevenue[m] = netImpl[m];
        deferredBalance[m] = 0;
      });
    } else {
      // Deferred mode: cohort-based recognition over the line's OWN term.
      // ★★ PART B — `length_months` REPLACES THE HARDCODED 12. Implementation is the
      //   NON-RENEWING engine (§7): a cohort runs fee/length for exactly `length` months and
      //   then STOPS. Its 12 was never a contract year — unlike AE/CQ, where the 12 IS the
      //   annual term and is correct (§12.5 rule 28). The operator's workbook carries the term
      //   per cohort (Imp Def Rev col C `Length`; E38 = $D38/$C38 divides each cohort's fee by
      //   ITS OWN length), and Part A added the column with deliberately NO DEFAULT so that a
      //   missing value is REACHABLE rather than silently 12.
      if (!termMonths) {
        // ★★ THE REFUSAL — ONE guard, ONE message, covering NULL / absent / 0 / negative /
        //   non-integer. A blank is FLAGGED, never silently defaulted (§E1; Brian 2026-09-14:
        //   a missing length is a TYPO and must fail loudly).
        // ⛔ WARN AND SKIP, NEVER THROW. This engine runs inside runFullModel on the RENDER
        //   path, so a throw is a BLANK PAGE, not a message anyone reads — the B5 K3 precedent
        //   (computeDA's missing purchase date) decided this exact question the same way.
        //   The line CONTRIBUTES NOTHING to IS, BS or CFS rather than amortising over an
        //   invented term: a self-consistent wrong answer is invisible, which is what makes it
        //   worth refusing rather than defaulting.
        const msg = `Implementation line "${line.name}" (id ${line.id}) is set to DEFERRED ` +
          `recognition but has no valid recognition length. length_months = ` +
          `${line.length_months === null || line.length_months === undefined ? 'not set' : JSON.stringify(line.length_months)}` +
          ` — it must be a whole number of months greater than 0. The line is EXCLUDED from ` +
          `the forecast until a length is entered; it is NOT recognised over a default term.`;
        warnings.push({ lineId: line.id, name: line.name, length_months: line.length_months ?? null, message: msg });
        console.warn('[computeImplementationRevenue] ' + msg);
        return; // contributes nothing — no result entry, no deposit, no leaf value
      }

      /* W-081 (2026-10-05): ONE ROUNDING PER MONTH, ON THE LINE'S CUMULATIVE RECOGNITION -- the operator's template's own
       * arithmetic (Def Rev template Implementation ver2.xlsx: each cohort row is $D/$C UNROUNDED, row 65 sums them, row 68 rolls
       * the balance). Each cohort used to be rounded per month (r2(fee/term)) with a last-month remainder; with 500/12 that
       * recognised 83.34 where the template has 83.33, and -- worse -- the EXISTING book's tail did not add up to the balance the
       * GL closed at: measured at a 2024-12 close, the balance settled at 2,749.93 against the template's 2,750.00, 0.07 stranded
       * for ever. Now every share is exact, the line's recognition is summed exactly, and each month books
       *   r2(cumulative to this month) - r2(cumulative to last month)
       * which CONSERVES by construction (what is recognised over a cohort's life is exactly its fee) and leaves the balance at
       * r2(the exact balance). Same shape as B6's r18 fix for AE ("one division, one rounding"). */
      const exact = {};
      forecastMonths.forEach(m => { exact[m] = 0; });

      // Step 1: New implementation cohorts from forecast months -- fee/term for exactly `term` months, then it STOPS.
      forecastMonths.forEach(m => {
        const impl = netImpl[m];
        if (impl === 0) return;
        for (let i = 0; i < termMonths; i++) {
          const recMonth = addMonths(m, i);
          if (exact[recMonth] !== undefined) exact[recMonth] += impl / termMonths;
        }
      });

      // Step 2: Actuals cohort recognition (existing mode only) — non-renewing
      // trailing-cohort DECAY run-off via the shared existing-book recognition core.
      if (startMode === 'existing') {
        const ebRev = recognizeExistingBook({
          renewing: false,
          forecastMonths: forecastMonths,
          closeMonth,
          cohorts: existingTotalImpl,
          // ★ PART B — the EXISTING book runs off over the SAME term as this line's new
          // cohorts. Leaving it at the shared default would have one line recognising its
          // new bookings over `length_months` and its existing book over 12 — one rule,
          // two answers, inside a single line.
          termMonths,
          exact: true,   // W-081: unrounded shares; the one rounding happens below, on the line's cumulative
        });
        forecastMonths.forEach(fm => {
          if (fm <= closeMonth) return;
          exact[fm] += ebRev[fm];
        });
      }

      // W-081: the one rounding -- each month's revenue is the change in the ROUNDED cumulative.
      {
        let cum = 0, cumRounded = 0;
        forecastMonths.forEach(m => {
          if (m <= closeMonth) return;
          cum += exact[m];
          const next = r2(cum);
          gaapRevenue[m] = r2(next - cumRounded);
          cumRounded = next;
        });
      }

      // Step 3: Deferred balance (floored at zero — cannot recognize more than remaining balance)
      let bal = startMode === 'existing' ? openingBsBalance : 0;
      forecastMonths.forEach(m => {
        if (m <= closeMonth) return;
        const implAmt = netImpl[m] || 0;
        const maxGaap = r2(bal + implAmt);
        const actualGaap = Math.min(gaapRevenue[m] || 0, maxGaap);
        bal = Math.max(0, r2(bal + implAmt - actualGaap));
        gaapRevenue[m] = actualGaap;
        deferredBalance[m] = bal;
      });
    }

    // ── Build output: only forecastMonths ──
    const out = {
      name: line.name,
      enabled: line.enabled,
      recognition_mode: recognitionMode,
      // ★ PART B — the routing target, passed through for runFullModel's deposit to resolve.
      // Mirrors computeMiscRevenue.js:49 exactly (`line.target_gl_line_key || null`). The
      // ENGINE does not route; it carries the operator's answer to the one place that does.
      target_gl_line_key: line.target_gl_line_key || null,
      // W-081: the balance-sheet answer, passed through like target_gl_line_key; the builder and the orchestrator resolve it.
      bs_line_key: line.bs_line_key || null,
      // 2026-10-27: the fee basis as the engine applied it ('pct_new_acv' | 'per_client' -- NULL reads as per client) and the
      // percentage (a fraction, % mode only; null otherwise), so the page can label its rows (step 3).
      impl_basis: pctBasis ? 'pct_new_acv' : 'per_client',
      pct_of_new_acv: pctBasis ? pctOfNewAcv : null,
      newClients: {},
      grossImpl: {},
      netImpl: {},
      gaapRevenue: {},
      deferredBalance: {},
      sourceNewACV: {},
    };

    forecastMonths.forEach(m => {
      out.newClients[m] = newClients[m] || 0;
      out.grossImpl[m] = grossImpl[m] || 0;
      out.netImpl[m] = netImpl[m] || 0;
      out.gaapRevenue[m] = gaapRevenue[m] || 0;
      out.deferredBalance[m] = deferredBalance[m] || 0;
      out.sourceNewACV[m] = sourceNewACV[m] || 0;
    });

    result[line.id] = out;
  });

  return result;
}

export { computeImplementationRevenue, r2 };
